import { useEffect, useState } from 'react';
import { Control, FieldValues, useFormContext, useWatch } from 'react-hook-form';

import { ReportSelectorOption } from '../../../../../../interfaces/ReportSelectorOption';
import { ReportSelectorParams } from '../../../../../../interfaces/ReportSelectorParams';
import { ReportSetting } from '../../../../../../interfaces/ReportSetting';
import { reportService } from '../../../../../../services/reportService';
import { salesReportStore } from '../../../../../../stores/salesReportStore';
import { getAllOption, AllOption } from '../settings/getAllOption';
import { getRules, SelectorRules } from '../settings/getRules';
import { getSelectorLabel } from '../settings/getSelectorLabel';

export type SelectorSettings = Omit<
  ReportSetting,
  'subSettings' | 'selectorFunctionalType' | 'description'
>;

export type ParentSelectorSettings = Pick<
  SelectorSettings,
  'parameterKey' | 'selectorType'
>;

export type UseSelectorProps = {
  selectorSettings: SelectorSettings;
  parentSelectorSettings?: ParentSelectorSettings;
  reportType: string;
};

type UseSelectorReturn = {
  control: Control<FieldValues>;
  options: (AllOption | ReportSelectorOption)[];
  label: string;
  parameterKey: string;
  rules: SelectorRules | undefined;
  isShowSelector: boolean;
};

export const useSelector = ({
  selectorSettings,
  parentSelectorSettings = { parameterKey: null, selectorType: '' },
  reportType,
}: UseSelectorProps): UseSelectorReturn => {
  const [options, setOptions] = useState<(AllOption | ReportSelectorOption)[]>([]);
  const { setValue, control } = useFormContext();

  const selectorType = selectorSettings.selectorType;
  const selectAllAvailable = selectorSettings.selectAllAvailable ?? false;
  const parameterKey = selectorSettings.parameterKey ?? '';
  const paymentType = selectorSettings.paymentType;
  const selectorLabel = selectorSettings.selectorLabel ?? '';

  const parentParameterKey = parentSelectorSettings?.parameterKey ?? '';
  const parentSelectorType = parentSelectorSettings?.selectorType ?? '';

  const parentValue = useWatch({
    name: parentParameterKey,
    defaultValue: null,
  });

  const getOptions = async () => {
    const selectorParams: ReportSelectorParams = {
      reportType,
      selectorType,
      parentSelectorType: parentSelectorType || undefined,
      parentResourceId: parentValue ?? undefined,
    };

    try {
      const { data } = await reportService.getReportSelectorOptions(selectorParams);
      processOptions(data);
    } catch (error) {
      salesReportStore.closeDownloadModal();
      throw error;
    }
  };

  const processOptions = (optionsList: ReportSelectorOption[]) => {
    if (selectAllAvailable) {
      const allOption = getAllOption(selectorLabel);
      setOptions([allOption, ...optionsList]);
      setValue(parameterKey, '');
    } else {
      setOptions(optionsList);
      setValue(parameterKey, null);
    }
  };

  useEffect(() => {
    if (!parentParameterKey || parentValue) {
      getOptions();
    }
  }, [parentValue]);

  const label = getSelectorLabel(paymentType, selectorLabel);
  const rules = getRules(selectAllAvailable, selectorLabel);
  const isShowSelector = !!options.length;

  return {
    control,
    options,
    label,
    parameterKey,
    rules,
    isShowSelector,
  };
};
